import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import '../ExplanationLesson/ExplanationLesson.css';
import './FormulaExplanationLesson.css';


/* =====================================================
   CARD SETTINGS
   ===================================================== */

const SWIPE_THRESHOLD = 55;
const ANIMATION_DURATION = 240;


/* =====================================================
   HELPERS
   ===================================================== */

const normalizeFormulaOptionKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');


const getExplanationItemHtml = (
  explanationItem
) => {
  if (
    typeof explanationItem === 'string'
  ) {
    return explanationItem;
  }

  if (
    !explanationItem ||
    typeof explanationItem !== 'object'
  ) {
    return '';
  }

  return (
    explanationItem.html ||
    explanationItem.description ||
    explanationItem.descriptionExplanation ||
    explanationItem.text ||
    ''
  );
};


/* =====================================================
   GET FORMULA OPTIONS
   ===================================================== */

const getFormulaExplanationRawOptions = (
  explanationItem,
  question,
  explanationIndex
) => {
  const descriptionItems = Array.isArray(
    question?.descriptionExplanation
  )
    ? question.descriptionExplanation
    : [];

  const isLastDescription =
    explanationIndex ===
    descriptionItems.length - 1;

  return (
    explanationItem?.options ??
    explanationItem?.formulaOptions ??
    (
      isLastDescription
        ? (
            question?.options ??
            question?.formulaOptions
          )
        : null
    )
  );
};


/* =====================================================
   SPLIT FORMULA ACCOUNTS
   ===================================================== */

const splitFormulaSideAccounts = (
  rawValue
) => {
  if (
    rawValue === null ||
    rawValue === undefined
  ) {
    return [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue
      .flatMap((item) =>
        splitFormulaSideAccounts(item)
      )
      .filter(Boolean);
  }

  if (typeof rawValue === 'object') {
    const objectAccount =
      rawValue.account ??
      rawValue.cont ??
      rawValue.name ??
      rawValue.label ??
      null;

    if (
      objectAccount === null ||
      objectAccount === undefined
    ) {
      return [];
    }

    const normalized =
      String(objectAccount).trim();

    return normalized
      ? [normalized]
      : [];
  }

  const value =
    String(rawValue).trim();

  if (!value) {
    return [];
  }

  const parts = value
    .split(
      /\s*\/\s*|\s*;\s*|\s*\|\s*|\n+/
    )
    .map((item) => item.trim())
    .filter(
      (item) =>
        item !== '' &&
        item !== '%'
    );

  return parts.length > 0
    ? parts
    : [value];
};


/* =====================================================
   FORMULA SIDES
   ===================================================== */

const getFormulaExplanationSides = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (!rawOptions) {
    return {
      leftAccounts: [],
      rightAccounts: [],
      operator: '=',
    };
  }

  if (Array.isArray(rawOptions)) {
    const left =
      rawOptions[0] !== undefined
        ? [
            String(
              rawOptions[0]
            ).trim(),
          ]
        : [];

    const operator =
      rawOptions[1] !== undefined
        ? String(
            rawOptions[1]
          ).trim()
        : '=';

    const right =
      rawOptions[2] !== undefined
        ? [
            String(
              rawOptions[2]
            ).trim(),
          ]
        : [];

    return {
      leftAccounts:
        left.filter(Boolean),

      rightAccounts:
        right.filter(Boolean),

      operator:
        operator || '=',
    };
  }

  if (
    typeof rawOptions !== 'object'
  ) {
    return {
      leftAccounts: [],
      rightAccounts: [],
      operator: '=',
    };
  }

  const explicitSides =
    rawOptions.sides &&
    typeof rawOptions.sides === 'object'
      ? rawOptions.sides
      : null;

  const getByAliases = (
    aliases
  ) => {
    const optionMatch =
      Object.entries(
        rawOptions
      ).find(([key]) =>
        aliases.includes(
          normalizeFormulaOptionKey(
            key
          )
        )
      );

    if (optionMatch) {
      return optionMatch[1];
    }

    if (explicitSides) {
      const sideMatch =
        Object.entries(
          explicitSides
        ).find(([key]) =>
          aliases.includes(
            normalizeFormulaOptionKey(
              key
            )
          )
        );

      if (sideMatch) {
        return sideMatch[1];
      }
    }

    return null;
  };

  const leftRaw =
    getByAliases([
      'cont1',
      'contul1',
      'account1',
      'leftaccount',
      'left',
      'debit',
      'debitaccounts',
      'leftaccounts',
    ]);

  const rightRaw =
    getByAliases([
      'cont2',
      'contul2',
      'account2',
      'rightaccount',
      'right',
      'credit',
      'creditaccounts',
      'rightaccounts',
    ]);

  const operatorRaw =
    getByAliases([
      'operator',
      'semn',
      'sign',
      'equals',
      'egal',
      'eq',
    ]);

  return {
    leftAccounts:
      splitFormulaSideAccounts(
        leftRaw
      ),

    rightAccounts:
      splitFormulaSideAccounts(
        rightRaw
      ),

    operator:
      String(
        operatorRaw ?? '='
      ).trim() || '=',
  };
};


/* =====================================================
   FORMULA PARTS
   ===================================================== */

const getFormulaExplanationParts = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (Array.isArray(rawOptions)) {
    return rawOptions
      .filter((part) => {
        if (
          part === null ||
          part === undefined
        ) {
          return false;
        }

        if (
          typeof part === 'object'
        ) {
          return false;
        }

        return (
          String(part).trim() !== ''
        );
      })
      .map(String);
  }

  if (
    !rawOptions ||
    typeof rawOptions !== 'object'
  ) {
    return [];
  }

  const ignoredKeys =
    new Set([
      'rows',
      'formularows',
      'compoundrows',
      'entries',
      'lines',
      'breakdown',
      'detalii',
      'detaliere',
    ]);

  const entries =
    Object.entries(
      rawOptions
    ).filter(
      ([key, value]) => {
        if (
          value === null ||
          value === undefined
        ) {
          return false;
        }

        if (
          typeof value === 'object'
        ) {
          return false;
        }

        if (
          ignoredKeys.has(
            normalizeFormulaOptionKey(
              key
            )
          )
        ) {
          return false;
        }

        return (
          String(value).trim() !== ''
        );
      }
    );

  const groupedKeys = [
    [
      'cont1',
      'contul1',
      'account1',
      'leftaccount',
      'left',
      'debit',
    ],

    [
      'operator',
      'semn',
      'sign',
      'equals',
      'egal',
      'eq',
    ],

    [
      'cont2',
      'contul2',
      'account2',
      'rightaccount',
      'right',
      'credit',
    ],

    [
      'suma',
      'sum',
      'amount',
      'valoare',
    ],
  ];

  const usedKeys =
    new Set();

  const orderedParts = [];

  groupedKeys.forEach(
    (keyGroup) => {
      const match =
        entries.find(([key]) =>
          keyGroup.includes(
            normalizeFormulaOptionKey(
              key
            )
          )
        );

      if (!match) {
        return;
      }

      usedKeys.add(match[0]);

      orderedParts.push(
        String(match[1])
      );
    }
  );

  entries.forEach(
    ([key, value]) => {
      if (
        !usedKeys.has(key)
      ) {
        orderedParts.push(
          String(value)
        );
      }
    }
  );

  return orderedParts;
};


/* =====================================================
   FORMULA ROWS
   ===================================================== */

const getFormulaExplanationRows = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (
    !rawOptions ||
    typeof rawOptions !== 'object'
  ) {
    return [];
  }

  const rowKeys = [
    'rows',
    'formulaRows',
    'compoundRows',
    'entries',
    'lines',
    'breakdown',
    'detalii',
    'detaliere',
  ];

  const rowCollection =
    rowKeys
      .map(
        (key) =>
          rawOptions[key]
      )
      .find(Array.isArray);

  if (
    !Array.isArray(
      rowCollection
    )
  ) {
    return [];
  }

  return rowCollection
    .map((row) => {
      if (
        typeof row === 'string'
      ) {
        const value =
          row.trim();

        return value
          ? {
              account: value,
              amount: '',
            }
          : null;
      }

      if (
        !row ||
        typeof row !== 'object'
      ) {
        return null;
      }

      const account =
        row.cont ??
        row.account ??
        row.accountCode ??
        row.code ??
        row.contul ??
        '';

      const amount =
        row.suma ??
        row.amount ??
        row.value ??
        row.valoare ??
        '';

      const normalizedAccount =
        String(account).trim();

      const normalizedAmount =
        String(amount).trim();

      if (
        !normalizedAccount &&
        !normalizedAmount
      ) {
        return null;
      }

      return {
        account:
          normalizedAccount,

        amount:
          normalizedAmount,
      };
    })
    .filter(Boolean);
};


/* =====================================================
   FORMULA CARD CONTENT
   ===================================================== */

const FormulaCard = ({
  question,
}) => {
  const descriptions =
    Array.isArray(
      question
        ?.descriptionExplanation
    )
      ? question
          .descriptionExplanation
      : [];

  const lastDescriptionIndex =
    Math.max(
      descriptions.length - 1,
      0
    );

  const formulaParts =
    getFormulaExplanationParts(
      question,
      question,
      lastDescriptionIndex
    );

  const formulaRows =
    getFormulaExplanationRows(
      question,
      question,
      lastDescriptionIndex
    );

  const formulaSides =
    getFormulaExplanationSides(
      question,
      question,
      lastDescriptionIndex
    );

  const hasCompoundSides =
    formulaSides
      .leftAccounts
      .length > 0 &&
    formulaSides
      .rightAccounts
      .length > 0;

  const hasMultipleAccounts =
    formulaSides
      .leftAccounts
      .length > 1 ||
    formulaSides
      .rightAccounts
      .length > 1;

  const hasMainFormula =
    formulaParts.length >= 3;

  return (
    <div className="formula-panel">

     

    {formulaRows.length > 0 &&
hasMainFormula ? (
  <div className="formula-grid">

    <div className="formula-grid-cell formula-grid-main">
      {formulaParts[0]}
    </div>

    <div className="formula-grid-cell formula-grid-operator">
      {formulaParts[1] || '='}
    </div>

    <div className="formula-grid-cell formula-grid-main">
      {formulaParts[2]}
    </div>

    {formulaRows.map(
      (
        row,
        rowIndex
      ) => {
        const rowsOnLeft =
          formulaParts[0] === '%';

        const rowsOnRight =
          formulaParts[2] === '%';

        return (
      <Fragment
  key={`formula-grid-row-${rowIndex}`}
>

            <div className="formula-grid-cell formula-grid-account">
              {rowsOnLeft
                ? row.account
                : ''}
            </div>

            <div className="formula-grid-cell formula-grid-operator-empty">
              {rowsOnLeft || rowsOnRight
                ? ''
                : '='}
            </div>

            <div className="formula-grid-cell formula-grid-account">
              {rowsOnRight
                ? row.account
                : ''}
            </div>

         </Fragment>
        );
      }
    )}

  </div>
) : hasCompoundSides &&
        hasMultipleAccounts ? (

        <div className="formula-compound-layout">

          <div className="formula-compound-side">
            {formulaSides
              .leftAccounts
              .map(
                (
                  account,
                  accountIndex
                ) => (
                  <span
                    key={
                      `left-${accountIndex}`
                    }
                    className="formula-compound-account"
                  >
                    {account}
                  </span>
                )
              )}
          </div>

          <span className="formula-token formula-token-operator">
            {formulaSides.operator || '='}
          </span>

          <div className="formula-compound-side">
            {formulaSides
              .rightAccounts
              .map(
                (
                  account,
                  accountIndex
                ) => (
                  <span
                    key={
                      `right-${accountIndex}`
                    }
                    className="formula-compound-account"
                  >
                    {account}
                  </span>
                )
              )}
          </div>

        </div>

      ) : hasMainFormula ? (

        <div className="formula-row">

          <span className="formula-token">
            {formulaParts[0]}
          </span>

          <span className="formula-token formula-token-operator">
            {formulaParts[1] || '='}
          </span>

          <span className="formula-token">
            {formulaParts[2]}
          </span>

          {formulaParts[3] && (
            <span className="formula-token">
              {formulaParts[3]}
            </span>
          )}

        </div>

      ) : formulaRows.length > 0 ? (

        <div className="formula-explanation-accounts-column">

          {formulaRows.map(
            (
              row,
              rowIndex
            ) => (
              <div
                key={
                  `formula-row-${rowIndex}`
                }
                className="formula-explanation-account-item"
              >
                <span className="accounting-note-account-cf">
                  {row.account}
                </span>

                {row.amount && (
                  <span className="accounting-note-amount-cf">
                    {row.amount}
                  </span>
                )}
              </div>
            )
          )}

        </div>

      ) : (

        <div className="formula-empty">
          Formula nu este disponibila.
        </div>

      )}

    </div>
  );
};


/* =====================================================
   FORMULA EXPLANATION LESSON
   ===================================================== */

const FormulaExplanationLesson =
  forwardRef(
    (
      {
        question,
        cardIndex = 0,
        onCardIndexChange,
        onUnlockContinue,
      },
      ref
    ) => {

      const descriptions =
        Array.isArray(
          question
            ?.descriptionExplanation
        )
          ? question
              .descriptionExplanation
          : [];

      /*
        Cardurile sunt:

        1. descriptionExplanation
        2. ultimul card = formula
      */

      const totalCards =
        descriptions.length + 1;


      const [
        leavingCardIndex,
        setLeavingCardIndex,
      ] = useState(null);


      const [
        swipeDirection,
        setSwipeDirection,
      ] = useState('left');


      const [
        isAnimating,
        setIsAnimating,
      ] = useState(false);


      const [
        dragOffset,
        setDragOffset,
      ] = useState({
        x: 0,
        y: 0,
      });


      const animationTimerRef =
        useRef(null);


      const pointerStateRef =
        useRef({
          active: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0,
          horizontal: false,
        });


      /* =====================================================
         RESET ON NEW QUESTION
         ===================================================== */

      useEffect(() => {

        setLeavingCardIndex(
          null
        );

        setSwipeDirection(
          'left'
        );

        setIsAnimating(
          false
        );

        setDragOffset({
          x: 0,
          y: 0,
        });


        pointerStateRef.current = {
          active: false,
          pointerId: null,
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0,
          horizontal: false,
        };


        if (
          animationTimerRef.current
        ) {
          clearTimeout(
            animationTimerRef.current
          );

          animationTimerRef.current =
            null;
        }


        onCardIndexChange?.(0);

      }, [
        question,
        onCardIndexChange,
      ]);


      /* =====================================================
         UNLOCK
         ===================================================== */

      useEffect(() => {
        if (
          totalCards <= 1
        ) {
          onUnlockContinue?.();
        }
      }, [
        totalCards,
        onUnlockContinue,
      ]);


      useEffect(() => {
        if (
          totalCards > 0 &&
          cardIndex ===
            totalCards - 1
        ) {
          onUnlockContinue?.();
        }
      }, [
        cardIndex,
        totalCards,
        onUnlockContinue,
      ]);


      /* =====================================================
         CLEANUP
         ===================================================== */

      useEffect(() => {
        return () => {
          if (
            animationTimerRef.current
          ) {
            clearTimeout(
              animationTimerRef.current
            );
          }
        };
      }, []);


      /* =====================================================
         RESET POINTER
         ===================================================== */

      const resetPointerState =
        useCallback(() => {

          pointerStateRef.current = {
            active: false,
            pointerId: null,
            startX: 0,
            startY: 0,
            lastX: 0,
            lastY: 0,
            horizontal: false,
          };

        }, []);


      /* =====================================================
         CHANGE CARD
         ===================================================== */

      const animateToCard =
        useCallback(
          (direction) => {

            if (
              isAnimating ||
              totalCards <= 1
            ) {
              resetPointerState();
              return;
            }


            const nextIndex =
              direction === 'left'
                ? (
                    cardIndex + 1
                  ) % totalCards
                : (
                    cardIndex -
                    1 +
                    totalCards
                  ) % totalCards;


            setIsAnimating(true);

            setSwipeDirection(
              direction
            );

            setLeavingCardIndex(
              cardIndex
            );


            setDragOffset({
              x:
                direction === 'left'
                  ? -window.innerWidth
                  : window.innerWidth,

              y: 0,
            });


            animationTimerRef.current =
              setTimeout(() => {

                onCardIndexChange?.(
                  nextIndex
                );


                setLeavingCardIndex(
                  null
                );

                setIsAnimating(
                  false
                );


                setDragOffset({
                  x: 0,
                  y: 0,
                });


                animationTimerRef.current =
                  null;

              }, ANIMATION_DURATION);

          },
          [
            cardIndex,
            isAnimating,
            onCardIndexChange,
            resetPointerState,
            totalCards,
          ]
        );


      /* =====================================================
         EXTERNAL NAVIGATION
         ===================================================== */

      useImperativeHandle(
        ref,
        () => ({
          nextCard: () => {
            animateToCard(
              'left'
            );
          },

          previousCard: () => {
            animateToCard(
              'right'
            );
          },
        }),
        [
          animateToCard,
        ]
      );


      /* =====================================================
         POINTER DOWN
         ===================================================== */

      const handlePointerDown =
        useCallback(
          (event) => {

            if (isAnimating) {
              return;
            }


            if (
              event.pointerType ===
                'mouse' &&
              event.button !== 0
            ) {
              return;
            }


            pointerStateRef.current = {
              active: true,

              pointerId:
                event.pointerId,

              startX:
                event.clientX,

              startY:
                event.clientY,

              lastX:
                event.clientX,

              lastY:
                event.clientY,

              horizontal: false,
            };


            event.currentTarget
              ?.setPointerCapture?.(
                event.pointerId
              );

          },
          [
            isAnimating,
          ]
        );


      /* =====================================================
         POINTER MOVE
         ===================================================== */

      const handlePointerMove =
        useCallback(
          (event) => {

            const state =
              pointerStateRef.current;


            if (
              !state.active ||
              state.pointerId !==
                event.pointerId ||
              isAnimating
            ) {
              return;
            }


            const deltaX =
              event.clientX -
              state.startX;


            const deltaY =
              event.clientY -
              state.startY;


            state.lastX =
              event.clientX;

            state.lastY =
              event.clientY;


            if (
              !state.horizontal &&
              (
                Math.abs(
                  deltaX
                ) > 8 ||
                Math.abs(
                  deltaY
                ) > 8
              )
            ) {
              state.horizontal =
                Math.abs(
                  deltaX
                ) >
                Math.abs(
                  deltaY
                );
            }


            if (
              !state.horizontal
            ) {
              return;
            }


            event.preventDefault();


            setDragOffset({
              x: deltaX,
              y:
                deltaY *
                0.08,
            });

          },
          [
            isAnimating,
          ]
        );


      /* =====================================================
         POINTER UP
         ===================================================== */

      const handlePointerUp =
        useCallback(
          (event) => {

            const state =
              pointerStateRef.current;


            if (
              !state.active ||
              state.pointerId !==
                event.pointerId
            ) {
              return;
            }


            const deltaX =
              state.lastX -
              state.startX;


            const direction =
              deltaX < 0
                ? 'left'
                : 'right';


            try {
              event.currentTarget
                ?.releasePointerCapture?.(
                  event.pointerId
                );
            } catch {
              // pointer deja eliberat
            }


            resetPointerState();


            if (
              state.horizontal &&
              Math.abs(
                deltaX
              ) >=
                SWIPE_THRESHOLD
            ) {
              animateToCard(
                direction
              );
            } else {
              setDragOffset({
                x: 0,
                y: 0,
              });
            }

          },
          [
            animateToCard,
            resetPointerState,
          ]
        );


      /* =====================================================
         POINTER CANCEL
         ===================================================== */

      const handlePointerCancel =
        useCallback(() => {

          resetPointerState();

          setDragOffset({
            x: 0,
            y: 0,
          });

        }, [
          resetPointerState,
        ]);


      /* =====================================================
         NO QUESTION
         ===================================================== */

      if (!question) {
        return null;
      }


      /* =====================================================
         CREATE CARD LIST
         ===================================================== */

      const cards = [
        ...descriptions.map(
          (
            explanation,
            index
          ) => ({
            type:
              'explanation',

            key:
              `description-${index}`,

            html:
              getExplanationItemHtml(
                explanation
              ),
          })
        ),

        {
          type: 'formula',
          key: 'formula-card',
        },
      ];


      /* =====================================================
         RENDER
         ===================================================== */

      return (
        <section className="explanation-lesson">

          {question
            .titleExplanation && (
            <h2 className="explanation-lesson__title">
              {
                question
                  .titleExplanation
              }
            </h2>
          )}


          {question.image && (
            <img
              src={
                question.image
              }
              alt={
                question
                  .titleExplanation ||
                'Explicatie formula'
              }
              className="explanation-lesson__image"
            />
          )}


          <div
            className="explanation-lesson__card-stack"
            onPointerDown={
              handlePointerDown
            }
            onPointerMove={
              handlePointerMove
            }
            onPointerUp={
              handlePointerUp
            }
            onPointerCancel={
              handlePointerCancel
            }
          >

            {cards.map(
              (
                card,
                index
              ) => {

                const offset =
                  (
                    index -
                    cardIndex +
                    totalCards
                  ) %
                  totalCards;


                const isLeaving =
                  index ===
                  leavingCardIndex;


                if (
                  offset > 3 &&
                  !isLeaving
                ) {
                  return null;
                }


                const isActive =
                  offset === 0 &&
                  !isLeaving;


                const isNext =
                  offset === 1 &&
                  !isLeaving;


                let transform =
                  'translate3d(0, 0, 0)';


                let opacity = 1;


                /* =========================================
                   LEAVING CARD
                   ========================================= */

                if (
                  isLeaving
                ) {

                  const exitX =
                    swipeDirection ===
                    'left'
                      ? '-115%'
                      : '115%';


                  transform = `
                    translate3d(
                      ${exitX},
                      0,
                      0
                    )
                    rotate(
                      ${
                        swipeDirection ===
                        'left'
                          ? '-6deg'
                          : '6deg'
                      }
                    )
                    scale(0.97)
                  `;


                  opacity = 0;
                }


                /* =========================================
                   ACTIVE CARD
                   ========================================= */

                else if (
                  isActive
                ) {

                  const rotation =
                    Math.max(
                      -8,
                      Math.min(
                        8,
                        dragOffset.x /
                          45
                      )
                    );


                  const dragScale =
                    Math.max(
                      0.965,
                      1 -
                        Math.abs(
                          dragOffset.x
                        ) /
                          5000
                    );


                  transform = `
                    translate3d(
                      ${dragOffset.x}px,
                      ${dragOffset.y}px,
                      0
                    )
                    rotate(
                      ${rotation}deg
                    )
                    scale(
                      ${dragScale}
                    )
                  `;


                  opacity =
                    Math.max(
                      0.72,
                      1 -
                        Math.abs(
                          dragOffset.x
                        ) /
                          700
                    );
                }


                /* =========================================
                   CARDS BEHIND
                   ========================================= */

                else {

                  const progress =
                    Math.min(
                      Math.abs(
                        dragOffset.x
                      ) /
                        SWIPE_THRESHOLD,
                      1
                    );


                  const rotations = [
                    0,
                    -2.2,
                    2.8,
                    -3.8,
                  ];


                  const translateX = [
                    0,
                    -7,
                    8,
                    -10,
                  ];


                  const translateY = [
                    0,
                    12,
                    22,
                    32,
                  ];


                  const scaleValues = [
                    1,
                    0.985,
                    0.97,
                    0.955,
                  ];


                  const baseRotation =
                    rotations[
                      offset
                    ] ?? 0;


                  const baseX =
                    translateX[
                      offset
                    ] ?? 0;


                  const baseY =
                    translateY[
                      offset
                    ] ?? 0;


                  const baseScale =
                    scaleValues[
                      offset
                    ] ??
                    0.95;


                  const animatedY =
                    baseY -
                    progress *
                      Math.min(
                        baseY,
                        10
                      );


                  const animatedScale =
                    baseScale +
                    progress *
                      (
                        1 -
                        baseScale
                      ) *
                      0.5;


                  transform = `
                    translate3d(
                      ${baseX}px,
                      ${animatedY}px,
                      0
                    )
                    rotate(
                      ${baseRotation}deg
                    )
                    scale(
                      ${animatedScale}
                    )
                  `;


                  opacity =
                    Math.max(
                      0.76,
                      1 -
                        offset *
                          0.08
                    );
                }


                return (
                  <article
                    key={
                      card.key
                    }
                    className={[
                      'explanation-lesson__card',

                      isActive
                        ? 'explanation-lesson__card--active'
                        : '',

                      isActive &&
                      dragOffset.x !== 0
                        ? 'explanation-lesson__card--dragging'
                        : '',

                      isNext
                        ? 'explanation-lesson__card--next'
                        : '',

                      isLeaving
                        ? 'explanation-lesson__card--leaving'
                        : '',

                      card.type ===
                      'formula'
                        ? 'formula-explanation-card-content'
                        : '',
                    ]
                      .filter(
                        Boolean
                      )
                      .join(' ')}
                    style={{
                      transform,
                      opacity,

                      zIndex:
                        isLeaving
                          ? 10
                          : totalCards -
                            offset,
                    }}
                  >

                    {card.type ===
                    'formula' ? (

                      <FormulaCard
                        question={
                          question
                        }
                      />

                    ) : (

                      <div
                        className="explanation-lesson__text"
                        dangerouslySetInnerHTML={{
                          __html:
                            card.html,
                        }}
                      />

                    )}

                  </article>
                );
              }
            )}

          </div>

        </section>
      );
    }
  );


FormulaExplanationLesson.displayName =
  'FormulaExplanationLesson';


export default FormulaExplanationLesson;